import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import moment from 'moment'
import {ConfirmDialog} from 'primereact/confirmdialog'
import {createContext, useCallback, useContext, useEffect, useRef} from 'react'
import {SystemRole} from '../../../Constants'
import {useAppConfig} from '../../../context/app/AppContext'
import {useUserHospital} from '../../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../../i18n/TranslateUtils'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {addressTypeOptions} from '../../address/AddressConstant'
import {useAuth} from '../../authentication/AuthProvider'
import {getPageElectrocardiograms} from '../../ecg/ECGEndPoint'
import {getPageExaminations} from '../../examination/ExaminationEndPoint'
import {getPageGeneralPractitioners} from '../../general-practioner/GeneralPractitionerEndPoint'
import {useGenericLogCRUD} from '../../generic-log/query-keys/GenericLogKeysManager'
import {getPageUserWithRoles} from '../../healthcare/HealthCareEndPoint'
import {getPageHospitals} from '../../hospital/HospitalEndPoint'
import {actionTableKeys} from '../../new-table/Constants'
import {getPageNextOfKins} from '../../next-of-kin/NextOfKinEndPoint'
import {getPageNotes} from '../../note/NoteEndPoint'
import {isArrayAndNotEmpty} from '../../observations/Utils'
import {getPagePathways} from '../../pathway/PathwayEndPoint'
import {patientKeys} from '../../patient/PatientConstants'
import {getPagePatients} from '../../patient/PatientEndPoint'
import {getPageUserRoles} from '../../role/UserRoleEndPoint'
import {getPageStaticList} from '../../static-list/StaticListEndPoint'
import {
    fileEvent,
    fileEventTypes,
    getFileEventKey,
    removeFileEventListener,
} from '../../styled-component/broadcast/FileEvent'
import {
    datasetType,
    exportRecordTypes,
    exportTypes,
} from '../../styled-component/Constant'
import {genericLogQueryType} from '../../styled-component/generic-log/GenericLogConstansts'
import {
    accommodationStatusList,
    diagnosticList,
    hcpTitle,
    identityWithHeldReasonList,
    materialStatusList,
    religionList,
    titleList,
} from '../../styled-component/patient/Constants'
import {importErrorType} from '../../styled-component/patient/PatientTableConstants'
import ReviewImportDataTable from '../../styled-component/ReviewImportDataTable'
import {
    saveAsExcelOrCsvFile,
    tableTypes,
} from '../../styled-component/StyledTableUtils'
import {getPageSupportTeams} from '../../support-teams/SupportTeamEndPoint'
import {getPageUserManagements} from '../../user-property/UserPropertyEndPoint'
import {convertKeyToLabel} from '../TableUtils'
import {patientImportHeader} from './ImportConstant'
const ImportNotificationContext = createContext({})

export const ImportNotificationProvider = ({children}) => {
    useSignals()

    const importData = useSignal([])
    const importDatasetType = useSignal(datasetType.advanced)
    const errorList = useSignal([])
    const {translateOptions, translateKeys} = TranslateUtils()

    const reviewTableRef = useRef(null)
    const visible = useSignal(false)
    const {user} = useAuth()
    const {currentHospital} = useUserHospital()
    const {userRole} = useAppConfig()
    const queryClient = useQueryClient()
    //#region Import Data

    const handleConvertData = useCallback(
        value => {
            if (!value || value?.length < 1) {
                return []
            }

            let holderErrorList = []
            let holderDataList = []
            for (let i = 0; i < value?.length; i++) {
                if (!value[i]?.success) {
                    holderErrorList.push({
                        field: value[i]?.key,
                        rowIndex: i, // Exclude header column index
                        errorType: importErrorType.notAcceptedValues,
                        message: value[i]?.message,
                    })

                    holderDataList.push(value[i]?.data)
                }
            }

            // Map UID for data list
            let dataListWithUID = []

            if (holderDataList?.length) {
                for (const dataItem of holderDataList) {
                    let isExistUID = true

                    while (isExistUID) {
                        let randUID = 'xxxx-xxxx-xxx-xxxx'.replace(
                            /[x]/g,
                            char => {
                                let rand = (Math.random() * 16) | 0
                                let value =
                                    char === 'x' ? rand : (rand & 0x3) | 0x8
                                return value.toString(16)
                            },
                        )

                        if (
                            !dataListWithUID?.length ||
                            (dataListWithUID?.length &&
                                !dataListWithUID?.some(
                                    item => item?.uid === randUID,
                                ))
                        ) {
                            dataListWithUID.push({...dataItem, uid: randUID})
                            isExistUID = false
                        }
                    }
                }
            }

            errorList.value = holderErrorList
            importData.value = dataListWithUID
        },
        [errorList, importData],
    )

    const handleImportNotification = useCallback(
        value => {
            if (!!value) {
                if (value && value?.data?.length > 0) {
                    handleConvertData(value?.data)

                    visible.value = true
                }

                const {genericLogKeys} = QueryKeyManager(
                    QueryKeyTypes.genericLog,
                    user?.id,
                )
                const {patientKeys} = QueryKeyManager(
                    QueryKeyTypes.patient,
                    user?.id,
                )

                queryClient.invalidateQueries(
                    genericLogKeys.pattern({
                        type: genericLogQueryType.importLog,
                    }),
                )
                queryClient.invalidateQueries(
                    genericLogKeys.pattern({
                        type: genericLogQueryType.importAndExport,
                    }),
                )
                queryClient.invalidateQueries(patientKeys.pattern())
            }
        },
        [handleConvertData, visible, importDatasetType],
    )

    const handleOnClickAction = useCallback(() => {
        reviewTableRef.current.showForm({
            importData: importData.value,
            errorList: errorList.value,
            headerList: patientImportHeader,
        })
    }, [errorList.value, importData.value, importDatasetType.value])

    const handleOnChangeImportData = value => {
        importData.value = value?.importData
        errorList.value = value?.errorList
    }

    const handleOnDeleteRow = uid => {
        if (!uid) {
            return
        }

        if (!importData.value || !importData.value?.length) {
            return
        }

        importData.value = importData.value?.filter(data => data?.uid === uid)
    }

    //#endregion

    //#region Export Data

    const exportDataRef = useRef([])

    const handleQueryData = async (pageable, filterRequest, tableType) => {
        if (!pageable || !filterRequest || !tableType) {
            return {}
        }

        let data = {}

        switch (tableType) {
            case tableTypes.patient:
                data = await getPagePatients(
                    user?.id,
                    pageable.pageNumber,
                    pageable.pageRows,
                    pageable.sortField,
                    pageable.sortOrder,
                    filterRequest,
                )
                break

            case tableTypes.pathway:
                data = await getPagePathways(
                    user?.id,
                    pageable.pageNumber,
                    pageable.pageRows,
                    pageable.sortField,
                    pageable.sortOrder,
                    filterRequest,
                )
                break
            case tableTypes.gp:
                data = await getPageGeneralPractitioners(
                    user?.id,
                    pageable.pageNumber,
                    pageable.pageRows,
                    pageable.sortField,
                    pageable.sortOrder,
                    filterRequest,
                )
                break
            case tableTypes.supportTeams:
                data = await getPageSupportTeams(
                    user?.id,
                    pageable.pageNumber,
                    pageable.pageRows,
                    pageable.sortField,
                    pageable.sortOrder,
                    filterRequest,
                )
                break
            case tableTypes.hospital:
                data = await getPageHospitals(
                    user?.id,
                    pageable.pageNumber,
                    pageable.pageRows,
                    pageable.sortField,
                    pageable.sortOrder,
                    filterRequest,
                )
                break
            case tableTypes.hcp:
                data = await getPageUserWithRoles(
                    user?.id,
                    pageable.pageNumber,
                    pageable.pageRows,
                    pageable.sortField,
                    pageable.sortOrder,
                    filterRequest,
                )
                break
            case tableTypes.examination:
                data = await getPageExaminations(
                    user?.id,
                    pageable.pageNumber,
                    pageable.pageRows,
                    pageable.sortField,
                    pageable.sortOrder,
                    filterRequest,
                )
                break
            case tableTypes.note:
                data = await getPageNotes(
                    user?.id,
                    pageable.pageNumber,
                    pageable.pageRows,
                    pageable.sortField,
                    pageable.sortOrder,
                    filterRequest,
                )
                break
            case tableTypes.relevantContact:
                data = await getPageNextOfKins(
                    user?.id,
                    pageable.pageNumber,
                    pageable.pageRows,
                    pageable.sortField,
                    pageable.sortOrder,
                    filterRequest,
                )
                break
            case tableTypes.ecg:
                data = await getPageElectrocardiograms(
                    user?.id,
                    pageable.pageNumber,
                    pageable.pageRows,
                    pageable.sortField,
                    pageable.sortOrder,
                    filterRequest,
                )
                break
            case tableTypes.hcpStaticList:
                data = await getPageStaticList(
                    user?.id,
                    pageable.pageNumber,
                    pageable.pageRows,
                    pageable.sortField,
                    pageable.sortOrder,
                    filterRequest,
                )
                break
            case tableTypes.hcpSupportTeam:
                data = await getPageSupportTeams(
                    user?.id,
                    pageable.pageNumber,
                    pageable.pageRows,
                    pageable.sortField,
                    pageable.sortOrder,
                    filterRequest,
                )
                break
            case tableTypes.hcpPatient:
                data = await getPageUserManagements(
                    user?.id,
                    pageable.pageNumber,
                    pageable.pageRows,
                    pageable.sortField,
                    pageable.sortOrder,
                    filterRequest,
                )
                break
            case tableTypes.hcpRole:
                data = await getPageUserRoles(
                    user?.id,
                    pageable.pageNumber,
                    pageable.pageRows,
                    pageable.sortField,
                    pageable.sortOrder,
                    filterRequest,
                )
                break
            default:
                break
        }

        return data
    }

    const keysToExtractName = [patientKeys.pathways, patientKeys.staticLists]

    const mapToName = stringArr => {
        let value = '-'
        let arr =
            typeof stringArr === 'string'
                ? JSON.parse(stringArr)
                : !!isArrayAndNotEmpty(stringArr)
                ? stringArr
                : []

        if (!!isArrayAndNotEmpty(arr)) {
            value = arr
                .filter(x => !!x.name)
                .map(x => x.name)
                .join(', ')
        }

        return value
    }

    const convertDataToExport = (key, value) => {
        let content = '-'
        if (!value) {
            return content
        }

        switch (key) {
            case patientKeys.title:
                content =
                    [...titleList, ...hcpTitle].find(
                        title => title.value === value,
                    )?.label || '-'
                break
            case patientKeys.gender:
                content = convertKeyToLabel(value)
                break
            case patientKeys.identityWithHeldReason:
                content =
                    translateOptions(identityWithHeldReasonList).find(
                        identity => identity.value === value,
                    ).label || '-'
                break
            case patientKeys.religion:
                content =
                    translateOptions(religionList).find(
                        religion => religion.value === value,
                    ).label || '-'
                break
            case patientKeys.maritalStatus:
                content =
                    translateOptions(materialStatusList).find(
                        marital => marital.value === value,
                    ).label || '-'
                break
            case patientKeys.accommodationStatus:
                content =
                    translateOptions(accommodationStatusList).find(
                        accommodation => accommodation.value === value,
                    ).label || '-'
                break
            case patientKeys.diagnostic:
                content =
                    translateOptions(diagnosticList).find(
                        diagnostic => diagnostic.value === value,
                    ).label || '-'
                break
            case patientKeys.type:
                content =
                    translateOptions(addressTypeOptions).find(
                        addressType => addressType.value === value,
                    ).label || '-'
                break

            case patientKeys.healthCarePractitioners:
                if (typeof value === 'string') {
                    let healthCarePractitioner = JSON.parse(value)
                    if (!!isArrayAndNotEmpty(healthCarePractitioner)) {
                        content = healthCarePractitioner
                            ?.filter(item => !!item?.firstName)
                            .map(
                                item =>
                                    `${item?.firstName} ${
                                        item?.lastName || ''
                                    }`,
                            )
                            .join(', ')
                    }
                }
                break
            case patientKeys.sicknessLevel:
                content = !!value ? convertKeyToLabel(value) : '-'
                break
            case 'hospitalIds': // HCP Table
                if (!value) {
                    content = 0
                } else {
                    content = value['hospitalIds']?.length
                }
                break
            case 'userRoleIds': // HCP Table
                if (!value) {
                    content = 0
                } else {
                    content = value['userRoleIds']?.length
                }
                break
            case 'userRoles': // HCP Table
                if (!value) {
                    content = `0${
                        value['userRoles']?.length
                            ? '/' + value['userRoles']?.length
                            : ''
                    } qualified roles`
                } else {
                    const numsQualified =
                        value['userRoles'] &&
                        value['userRoles'].filter(item => item?.qualified)
                            ?.length
                    content = `${numsQualified}${
                        value['userRoles']?.length
                            ? '/' + value['userRoles']?.length
                            : ''
                    } qualified roles`
                }
                break
            case 'children': // Hospital Table
                if (!value['children'] || !value['children']?.length) {
                    content = '0'
                } else {
                    content = value['children']?.length
                }
                break
            default:
                if (keysToExtractName.includes(key)) {
                    content = mapToName(value)
                } else if (typeof value === 'boolean') {
                    content = value ? 'Yes' : 'No'
                } else {
                    content = value || '-'
                }
                break
        }

        return content
    }

    const queryDataForExport = async (
        exportOption,
        queryData,
        pageable,
        filterRequest,
        tableType,
    ) => {
        const rowPerPage = 100
        const maxMoreRow = 50
        let dataList = []

        if (
            exportOption?.exportRecordType === exportRecordTypes.inViewRecords
        ) {
            dataList = dataList.concat(queryData.content)
        } else if (
            exportOption?.exportRecordType === exportRecordTypes.allRecords
        ) {
            if (queryData?.totalElements <= rowPerPage + maxMoreRow) {
                const data = await handleQueryData(
                    {
                        ...pageable,
                        pageRows: queryData?.totalElements,
                    },
                    filterRequest,
                    tableType,
                )

                if (data && Object.keys(data)?.length > 0) {
                    dataList = dataList.concat(data.content)
                }
            } else {
                let remainingRow = queryData?.totalElements

                while (remainingRow > 0) {
                    let remainingFinalRows = remainingRow % rowPerPage

                    const data = await handleQueryData(
                        {
                            ...pageable,
                            pageRows:
                                remainingFinalRows <= maxMoreRow
                                    ? rowPerPage + remainingFinalRows
                                    : rowPerPage,
                        },
                        filterRequest,
                        tableType,
                    )

                    if (data && Object.keys(data)?.length > 0) {
                        dataList = dataList.concat(data.content)
                    }

                    if (remainingFinalRows <= maxMoreRow) {
                        remainingRow -= rowPerPage + remainingFinalRows
                    } else {
                        remainingRow -= rowPerPage
                    }
                }
            }
        }

        return dataList
    }

    const {mutateAsync: createOrUpdateGenericLog} = useGenericLogCRUD(
        QueryCRUDTypes.create,
        user?.id,
    )

    const saveExportLog = async (tableType, exportOption, dataList) => {
        if (!dataList || !dataList?.length) {
            return
        }

        const toPostData = {
            refId: user?.id,
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
            type: 'exportLog',
            data: {
                exportOption: exportOption,
                totalRecords: dataList?.length,
                type: tableType,
            },
            importExportLogType: tableType,
        }

        const data = await createOrUpdateGenericLog(toPostData)

        if (data?.genericId) {
            const {genericLogKeys} = QueryKeyManager(
                QueryKeyTypes.genericLog,
                user?.id,
            )

            queryClient.invalidateQueries({
                queryKey: genericLogKeys.pattern({
                    type: genericLogQueryType.exportLog,
                }),
            })

            queryClient.invalidateQueries({
                queryKey: genericLogKeys.pattern({
                    type: genericLogQueryType.importAndExport,
                }),
            })
        }
    }

    const handleOnExportDatatable = useCallback(
        async (
            exportOption,
            queryData,
            pageable,
            filterRequest,
            columns,
            tableType,
        ) => {
            if (
                !tableType ||
                !columns ||
                columns?.length < 1 ||
                !exportOption?.exportType
            ) {
                return
            }

            let dataList = []

            // Check if ref already contains the export data for this key. If it does, retrieve it to continue the export.
            const key = `${tableType}-${exportOption?.exportType}-${exportOption?.exportRecordType}-${exportOption?.exportColumns}`
            if (!!exportDataRef.current || exportDataRef?.length > 0) {
                const exportRefItem = exportDataRef.current.find(
                    exportDataItem => exportDataItem?.key === key,
                )

                if (exportRefItem) {
                    dataList = exportRefItem?.dataList
                } else {
                    dataList = await queryDataForExport(
                        exportOption,
                        queryData,
                        pageable,
                        filterRequest,
                        tableType,
                    )

                    exportDataRef.current.push({
                        key: key,
                        dataList: dataList,
                    })
                }
            }

            columns = columns?.filter(
                item => item?.field !== actionTableKeys.action,
            )

            if (dataList && dataList?.length > 0) {
                // Get Data format by Header column order
                let filteredColumnData = []

                for (let i = 0; i < dataList?.length; i++) {
                    let dataHolder = {}
                    for (const {field} of columns) {
                        if (
                            (field === 'hospitalIds' ||
                                field === 'userRoleIds' ||
                                field === 'userRoles') &&
                            tableType === tableTypes.hcp
                        ) {
                            dataHolder[field] = convertDataToExport(
                                field,
                                dataList[i],
                            )
                        } else {
                            const key =
                                tableType === tableTypes.patient &&
                                field === patientKeys.sicknessLevel
                                    ? patientKeys.sicknessStatus
                                    : field
                            dataHolder[field] =
                                convertDataToExport(field, dataList[i][key]) ||
                                '-'
                        }
                    }

                    filteredColumnData.push(dataHolder)
                }

                if (
                    exportOption.exportType === exportTypes.csv ||
                    exportOption.exportType === exportTypes.excel
                ) {
                    // Get header label
                    const headerLabels = []
                    if (columns && columns?.length > 0) {
                        for (const {header} of columns) {
                            headerLabels.push(header)
                        }
                    }

                    saveAsExcelOrCsvFile(
                        exportOption?.exportType === exportTypes.excel
                            ? 'xlsx'
                            : 'csv',
                        headerLabels,
                        filteredColumnData,
                        `${convertKeyToLabel(tableType).replace(
                            ' ',
                            '_',
                        )}_DataTable`,
                    )

                    // Save Log
                    saveExportLog(tableType, exportOption, dataList)
                } else if (exportOption.exportType === exportTypes.pdf) {
                    let exportColsMapped = []

                    exportColsMapped = columns.map(item => ({
                        header: item?.header,
                        dataKey: item?.field,
                    }))
                    import('jspdf').then(jsPDF => {
                        import('jspdf-autotable').then(() => {
                            const doc = new jsPDF.default({
                                orientation: 'landscape',
                            })

                            doc.text(
                                `Export in ${moment().toLocaleString()}`,
                                14,
                                24,
                            )

                            doc.autoTable(
                                exportColsMapped,
                                filteredColumnData,
                                {
                                    theme: 'striped',
                                    startY: 28,
                                    horizontalPageBreakBehaviour:
                                        'afterAllRows',
                                    horizontalPageBreak: true,
                                    showHead: 'everyPage',
                                    styles: {
                                        overflow: 'linebreak',
                                        cellWidth: 'wrap',
                                        cellPadding: 1,
                                        fontSize: 6,
                                    },
                                    columnStyles: {
                                        pathways: {
                                            cellWidth: 'wrap',
                                            overflow: 'linebreak',
                                        },
                                    },
                                },
                            )
                            doc.save(
                                `${convertKeyToLabel(tableType).replace(
                                    ' ',
                                    '_',
                                )}_DataTable_Export_${moment().toString()}.pdf`,
                            )
                        })
                    })

                    // Save Log
                    saveExportLog(tableType, exportOption, dataList)
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [user?.id],
    )

    //#endregion

    //#region Event Listener
    useEffect(() => {
        fileEvent.on(
            `${getFileEventKey(
                tableTypes.patient,
                fileEventTypes.NOTIFY_IMPORT_RESULT,
            )}`,
            value => handleImportNotification(value),
        )

        return () => {
            removeFileEventListener(
                tableTypes.patient,
                fileEventTypes.NOTIFY_IMPORT_RESULT,
            )
        }
    }, [handleImportNotification])
    //#endregion

    return (
        <ImportNotificationContext.Provider
            value={{
                handleOnExportDatatable: handleOnExportDatatable,
            }}>
            {children}
            <ReviewImportDataTable
                ref={reviewTableRef}
                isAfterImport={true}
                handleOnChangeImportData={handleOnChangeImportData}
                handleOnDeleteRow={handleOnDeleteRow}
            />
            <ConfirmDialog
                visible={visible.value}
                onHide={() => (visible.value = false)}
                message={
                    <div>
                        Request to review the data conflicts from the import.
                    </div>
                }
                header="Import - Actions Required"
                icon="pi pi-exclamation-triangle"
                accept={handleOnClickAction}
                reject={() => (visible.value = false)}
            />
        </ImportNotificationContext.Provider>
    )
}

export const useImportNotificationContext = () =>
    useContext(ImportNotificationContext)
